<style>
tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>

<template>
  <v-container>
    <!-- Filters -->
    <v-form class="mx-auto" style="max-width:600px;">
      <v-row>
        <v-col>
          <v-menu
            v-model="datepickers.start.open"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.startdate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                clearable
                hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="form.startdate"
              no-title
              @input="datepickers.start.open = false"
            />
          </v-menu>
        </v-col>

        <v-col>
          <v-menu
            v-model="datepickers.end.open"
            :disabled="!form.startdate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="form.enddate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                clearable
                hide-details
                :disabled="!form.startdate"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="form.enddate"
              no-title
              :min="form.startdate"
              @input="datepickers.end.open = false"
            />
          </v-menu>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-text-field
            v-model="form.searchtext"
            label="Search"
            hide-details
            prepend-icon="mdi-magnify"
            clearable
            @keypress.enter="search"
          >
            <template v-slot:append>
              <v-btn
                depressed tile small color="primary" class="ma-0"
                @click="search"
              >Search</v-btn>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-form>

    <!-- Totals / actions -->
    <v-row class="mt-4">
      <v-col class="d-flex align-center">
        <v-chip class="mr-2" small color="primary" text-color="white">
          Summary rows: {{ summary.length }}
        </v-chip>
        <v-chip class="mr-2" small color="primary" text-color="white">
          Users: {{ usersTotal }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn small outlined class="mr-2" @click="exportSummaryCsv">Export Summary CSV</v-btn>
        <v-btn small outlined @click="exportUsersCsv">Export Users CSV</v-btn>
      </v-col>
    </v-row>

    <!-- Summary table -->
    <v-data-table
      dense
      :headers="headers"
      :items="summarySorted"
      :items-per-page="25"
      class="elevation-1 mt-2"
      item-key="affiliate_id_referralcode_id"
      show-expand
      :expanded.sync="expanded"
      :footer-props="{ itemsPerPageOptions:[ -1, 15, 25, 50 ] }"
      :header-props="{ sortIcon: null }"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Referral Summary</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-switch
            v-model="hideZero"
            label="Hide zero-signup codes"
            class="mt-6"
            dense
          />
        </v-toolbar>
      </template>

      <!-- Row -->
      <template v-slot:item="{ item, expand, isExpanded }">
        <tr>
          <td>{{ item.affiliate_name }}</td>
          <td>{{ item.description }}</td>
          <td class="text-right">{{ item.numusers }}</td>
          <td class="px-0" style="width: 32px;">
            <v-icon @click="expand(!isExpanded)">
              {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </td>
        </tr>
      </template>

      <!-- Expanded: users under this referral code -->
      <template v-slot:expanded-item="{ headers, item }">
        <tr>
          <td></td>
          <td :colspan="headers.length - 2">
            <v-card flat class="pa-3">
              <div class="subtitle-2 mb-2">
                Users for: <strong>{{ item.affiliate_name }}</strong> — <em>{{ item.description }}</em>
              </div>

              <v-data-table
                dense
                :headers="userHeaders"
                :items="usersByReferral(item.referralcode_id)"
                :items-per-page="10"
                class="elevation-0"
                :footer-props="{ itemsPerPageOptions:[ -1, 10, 25, 50 ] }"
              >
                <template v-slot:item.name="{ item: u }">
                  {{ u.firstname }} {{ u.lastname }}
                </template>
              </v-data-table>
            </v-card>
          </td>
          <td></td>
        </tr>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import InviteService from '@/services/InviteService'

export default {
  name: 'ReferralSummary',
  data () {
    return {
      form: {
        startdate: null,
        enddate: null,
        searchtext: null
      },
      datepickers: { start: { open: false }, end: { open: false } },
      summary: [],
      users: [],
      expanded: [],
      hideZero: true,
      headers: [
        { text: 'Affiliate', value: 'affiliate_name' },
        { text: 'Referral Description', value: 'description' },
        { text: 'Users', value: 'numusers', align: 'right' },
        { text: '', value: 'data-table-expand' }
      ],
      userHeaders: [
        { text: 'ID', value: 'id', align: 'right' },
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Nickname', value: 'nickname' },
        { text: 'Cell', value: 'cellphone' },
        { text: 'Created', value: 'created' }
      ]
    }
  },
  computed: {
    usersTotal () {
      return this.users.length
    },
    summarySorted () {
      const rows = this.hideZero ? this.summary.filter(s => Number(s.numusers) > 0) : this.summary
      return rows
        .map(r => ({ ...r, affiliate_id_referralcode_id: `${r.affiliate_id}-${r.referralcode_id}` }))
        .sort((a, b) => {
          const an = (a.affiliate_name || '').localeCompare(b.affiliate_name || '')
          if (an !== 0) return an
          return (a.description || '').localeCompare(b.description || '')
        })
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    init () {
      this.search()
    },
    async search () {
      const payload = {
        startDate: this.form.startdate || null,
        endDate: this.form.enddate || null,
        search: this.form.searchtext || ''
      }

      try {
        this.$updateloading && this.$updateloading(1)
        const { data } = await InviteService.referralSummary(payload)
        this.summary = (data.summary || []).map(s => ({
          affiliate_id: s.affiliate_id,
          affiliate_name: s.affiliate_name,
          referralcode_id: s.referralcode_id,
          description: s.description,
          numusers: Number(s.numusers || 0)
        }))
        this.users = (data.users && data.users.results) ? data.users.results : []
      } catch (e) {
        console.error('Failed to load referral summary', e)
        this.$toast && this.$toast.error('Failed to load referral summary')
      } finally {
        this.$updateloading && this.$updateloading(-1)
      }
    },
    usersByReferral (referralcode_id) {
      return this.users.filter(u => Number(u.referralcode_id) === Number(referralcode_id))
    },
    exportSummaryCsv () {
      const rows = [
        ['Affiliate', 'Referral Description', 'Users'],
        ...this.summarySorted.map(s => [s.affiliate_name, s.description, s.numusers])
      ]
      this.downloadCsv(rows, 'referral-summary.csv')
    },
    exportUsersCsv () {
      const rows = [
        ['ID', 'First', 'Last', 'Email', 'Nickname', 'Cell', 'Created', 'Affiliate ID', 'ReferralCode ID'],
        ...this.users.map(u => [
          u.id, u.firstname, u.lastname, u.email || '', u.nickname || '', u.cellphone || '', u.created || '', u.affiliate_id || '', u.referralcode_id || ''
        ])
      ]
      this.downloadCsv(rows, 'referral-users.csv')
    },
    downloadCsv (rows, filename) {
      const csv = rows.map(r =>
        r.map(v => {
          const s = (v === null || v === undefined) ? '' : String(v)
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        }).join(',')
      ).join('\n')

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>
